import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ExternalLink, Loader2, X } from 'lucide-react';
import { dataDictionaryApi } from './dataDictionaryApi';
import FolderPicker from './FolderPicker';
import TagPicker from './TagPicker';
import { cleanTags, errorMessage, formatRelative, normalizeList, rowKey, sameList } from './dataDictionaryUtils';
import { vendorTokensIn } from '../../utils/signalProviders';

const DESCRIPTION_MAX = 2000;
const DISPLAY_NAME_MAX = 200;

function databricksUrl(row, workspaceUrl) {
  const host = String(workspaceUrl || '').replace(/\/$/, '');
  if (!host || !row?.catalog || !row?.schema || !row?.table_name) return '';
  return `${host}/explore/data/${encodeURIComponent(row.catalog)}/${encodeURIComponent(row.schema)}/${encodeURIComponent(row.table_name)}`;
}

function Field({ label, hint, children, right }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-semibold tracking-[0.02em] text-[#71717A]">{label}</span>
        {right}
      </div>
      {children}
      {hint ? <p className="text-xs text-zinc-400">{hint}</p> : null}
    </div>
  );
}

function draftFromRow(row) {
  return {
    display_name: row?.display_name || '',
    description: row?.description || '',
    visible: row?.visible === true,
    tags: cleanTags(row?.tags),
    folder_list: normalizeList(row?.folder_list),
  };
}

function diff(row, draft) {
  const base = draftFromRow(row);
  const changes = {};
  if (draft.display_name.trim() !== base.display_name) changes.display_name = draft.display_name.trim();
  if (draft.description.trim() !== base.description) changes.description = draft.description.trim();
  if (draft.visible !== base.visible) changes.visible = draft.visible;
  if (!sameList(draft.tags, base.tags)) changes.tags = draft.tags;
  if (!sameList(draft.folder_list, base.folder_list)) changes.folder_list = draft.folder_list;
  return changes;
}

/**
 * Right-hand editor for one column. Edits are local until Save, which sends every
 * changed field in a single PATCH.
 */
export default function ColumnDetailPanel({
  row,
  position,
  total,
  onClose,
  onPrev,
  onNext,
  onSaved,
  folders,
  tagOptions,
  dirtyRef,
  workspaceUrl,
}) {
  const [draft, setDraft] = useState(() => draftFromRow(row));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const key = rowKey(row);

  useEffect(() => {
    setDraft(draftFromRow(row));
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, row?.updated_at]);

  const changes = useMemo(() => diff(row, draft), [row, draft]);
  const dirty = Object.keys(changes).length > 0;
  const vendorLint = useMemo(
    () =>
      vendorTokensIn([
        draft.display_name,
        draft.description,
        (draft.folder_list || []).join('/'),
        (draft.tags || []).join(','),
      ]),
    [draft.display_name, draft.description, draft.folder_list, draft.tags]
  );
  useEffect(() => {
    if (dirtyRef) dirtyRef.current = dirty;
  }, [dirty, dirtyRef]);
  useEffect(
    () => () => {
      if (dirtyRef) dirtyRef.current = false;
    },
    [dirtyRef]
  );

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError('');
    try {
      const res = await dataDictionaryApi.updateRow(row, changes);
      onSaved?.({ ...row, ...(res?.hit || {}) }, changes);
    } catch (e) {
      setError(errorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const discard = () => setDraft(draftFromRow(row));
  const link = databricksUrl(row, workspaceUrl);

  return (
    <div className="flex h-full min-h-0 flex-col" onKeyDown={(e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        save();
      }
    }}>
      <div className="flex items-center justify-between border-b border-hairline px-4 py-3">
        <div className="flex items-center gap-2 text-xs text-[#71717A]">
          {position != null && total != null ? (
            <span>
              Column <b className="font-semibold text-[#18181B]">{position}</b> of {total.toLocaleString()}
            </span>
          ) : null}
          <span className="inline-flex gap-1">
            <button type="button" onClick={onPrev} disabled={!onPrev} className="inline-flex h-6 w-6 items-center justify-center rounded border border-hairline text-zinc-500 hover:bg-[#E8EEF5] disabled:opacity-40" aria-label="Previous column" title="Previous (↑)">
              <ArrowUp size={12} aria-hidden />
            </button>
            <button type="button" onClick={onNext} disabled={!onNext} className="inline-flex h-6 w-6 items-center justify-center rounded border border-hairline text-zinc-500 hover:bg-[#E8EEF5] disabled:opacity-40" aria-label="Next column" title="Next (↓)">
              <ArrowDown size={12} aria-hidden />
            </button>
          </span>
        </div>
        <button type="button" onClick={onClose} className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-[#E8EEF5] hover:text-[#18181B]" aria-label="Close panel">
          <X size={16} aria-hidden />
        </button>
      </div>

      <div className="flex items-center justify-between gap-2 border-b border-hairline bg-[#F8FAFC] px-4 py-3">
        <span className={`text-xs ${dirty ? 'text-amber-700' : 'text-[#71717A]'}`}>
          {dirty ? `${Object.keys(changes).length} unsaved change${Object.keys(changes).length === 1 ? '' : 's'}` : row.updated_at ? `Saved · ${formatRelative(row.updated_at)}` : 'No changes'}
        </span>
        <div className="flex gap-2">
          <button type="button" onClick={discard} disabled={!dirty || saving} className="h-8 rounded-md px-3 text-[13px] font-medium text-[#71717A] hover:bg-[#E8EEF5] hover:text-[#18181B] disabled:opacity-40">
            Discard
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[#0062FB] px-3 text-[13px] font-medium text-white hover:bg-[#0052E0] disabled:opacity-50"
            title="Save (⌘↵)"
          >
            {saving ? <Loader2 size={13} className="animate-spin" aria-hidden /> : null}
            Save
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold tracking-[0.02em] text-[#71717A]">Source</span>
          <div className="flex flex-wrap items-center gap-x-1 font-mono text-xs leading-[18px]">
            <span className="text-zinc-400">{row.catalog || 'bengen-workspace'}</span>
            <span className="text-zinc-300">/</span>
            <span className="text-[#71717A]">{row.schema}</span>
            <span className="text-zinc-300">/</span>
            <span className="text-[#71717A]">{row.table_name}</span>
            <span className="text-zinc-300">/</span>
            <span className="break-all font-semibold text-[#18181B]">{row.column_name}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-[#71717A]">
            {row.example ? <span className="truncate" title={`Example: ${row.example}`}>e.g. {row.example}</span> : null}
            {row.has_value_list ? <span>Has value list</span> : null}
            {row.is_active === false ? <span className="text-amber-700">Inactive in source</span> : null}
            {link ? (
              <a href={link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0062FB] hover:underline">
                Open in Databricks <ExternalLink size={11} aria-hidden />
              </a>
            ) : null}
          </div>
        </div>

        <Field label="Display name">
          <input
            type="text"
            value={draft.display_name}
            maxLength={DISPLAY_NAME_MAX}
            onChange={(e) => setDraft((d) => ({ ...d, display_name: e.target.value }))}
            placeholder={row.column_name}
            aria-label="Display name"
            className="h-[38px] w-full rounded-lg border border-hairline bg-surface px-3 text-sm placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#18181B]/10"
          />
        </Field>

        <Field
          label="Description"
          hint="Shown as help text in the segment builder."
          right={<span className="text-[11px] tabular-nums text-zinc-400">{draft.description.length} / {DESCRIPTION_MAX}</span>}
        >
          <textarea
            value={draft.description}
            maxLength={DESCRIPTION_MAX}
            rows={4}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            placeholder="What this column means, units, and any caveats."
            aria-label="Description"
            className="w-full resize-y rounded-lg border border-hairline bg-surface px-3 py-2 text-sm leading-5 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#18181B]/10"
          />
        </Field>

        <Field label="Folder">
          <FolderPicker value={draft.folder_list} onChange={(folder_list) => setDraft((d) => ({ ...d, folder_list }))} folders={folders} />
        </Field>

        <Field label="Tags">
          <TagPicker value={draft.tags} onChange={(tags) => setDraft((d) => ({ ...d, tags }))} options={tagOptions} />
        </Field>

        {vendorLint ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" role="status">
            Display name, description, folder, or tags include a data-vendor name. Segment Builder
            users will see it as Signal Provider ## — consider replacing the brand here.
          </p>
        ) : null}

        <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-hairline px-3.5 py-2.5">
          <span className="flex flex-col">
            <span className="text-sm font-medium text-[#18181B]">Visible in segment builder</span>
            <span className="text-xs text-[#71717A]">Hidden columns stay searchable here.</span>
          </span>
          <input
            type="checkbox"
            className="peer sr-only"
            checked={draft.visible}
            onChange={(e) => setDraft((d) => ({ ...d, visible: e.target.checked }))}
            aria-label="Visible in segment builder"
          />
          <span className={`relative h-5 w-9 shrink-0 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[#0062FB]/30 ${draft.visible ? 'bg-emerald-500' : 'bg-zinc-300'}`} aria-hidden>
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${draft.visible ? 'left-[18px]' : 'left-0.5'}`} />
          </span>
        </label>

        {error ? (
          <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
